import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Link2, Save, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export function AdminLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.msgLinks();
      setLinks(data.links || []);
      const d = {};
      (data.links || []).forEach((l) => { d[l.key] = { url: l.url, active: l.active }; });
      setDraft(d);
    } catch { toast.error("Failed to load links"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    setSavingKey(key);
    try {
      const { data } = await endpoints.msgLinkUpdate(key, draft[key]);
      setLinks((ls) => ls.map((l) => (l.key === key ? data : l)));
      toast.success("Link saved — new messages will use it immediately");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingKey(null);
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-3" data-testid="admin-links">
      <p className="text-sm text-slate-400">Edit the links sent in automated messages. Use the variable name (e.g. <span className="font-mono text-[#E11D2A]">{"{payment_link}"}</span>) inside any message template — it always resolves to the active URL here.</p>
      {links.map((l) => {
        const d = draft[l.key] || { url: l.url, active: l.active };
        const dirty = d.url !== l.url || d.active !== l.active;
        return (
          <div key={l.key} data-testid={`link-row-${l.key}`} className="p-4 rounded-xl bg-[#0F131A] border border-[#273042]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-[#E11D2A]" />
                <span className="font-semibold text-white">{l.label}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E11D2A]/10 text-[#E11D2A] font-mono">{`{${l.var}}`}</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-slate-400">{d.active ? "Active" : "Off"}</span>
                <button onClick={() => setDraft((p) => ({ ...p, [l.key]: { ...d, active: !d.active } }))}
                  data-testid={`link-active-${l.key}`}
                  className={`w-10 h-5.5 rounded-full relative transition-colors ${d.active ? "bg-[#E11D2A]" : "bg-slate-600"}`} style={{ height: 22, width: 40 }}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${d.active ? "left-[20px]" : "left-0.5"}`} />
                </button>
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={d.url} onChange={(e) => setDraft((p) => ({ ...p, [l.key]: { ...d, url: e.target.value } }))}
                data-testid={`link-url-${l.key}`} placeholder="https://bbkigali.com/..."
                className="flex-1 h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
              <button onClick={() => save(l.key)} disabled={!dirty || savingKey === l.key} data-testid={`link-save-${l.key}`}
                className="h-10 px-4 rounded-lg bg-[#E11D2A] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                {savingKey === l.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1">
              <Check className="w-3 h-3 text-green-400" /> Currently active: <span className="font-mono text-slate-300">{l.active ? l.url : "— (disabled, default used)"}</span>
            </p>
          </div>
        );
      })}
    </div>
  );
}
